import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const btn = "border border-[var(--phosphor)] px-2 py-1 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40";
const field = "bg-black border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] text-[var(--phosphor)] px-2 py-1 outline-none";

export default function TvSyncPanel({
  apiBase,
  authHeaders,
  channels,
  videosByChannel,
  selectedChannel,
  append,
  onUploaded,
  showLinkRequest = null,
  onShowLinkResolved = null,
}) {
  const [open] = useState(true);
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unparsedOpen, setUnparsedOpen] = useState(false);
  const [unparsedItems, setUnparsedItems] = useState([]);
  const [unparsedLoading, setUnparsedLoading] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [healthFilter, setHealthFilter] = useState("ALL");
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataHealth, setMetadataHealth] = useState(null);
  const [metadataBusy, setMetadataBusy] = useState(false);
  const [numberingPreview, setNumberingPreview] = useState(null);
  const [numberingOpen, setNumberingOpen] = useState(false);
  const [showLinkOpen, setShowLinkOpen] = useState(false);
  const [showLinkData, setShowLinkData] = useState(null);
  const [showLinkQuery, setShowLinkQuery] = useState("");
  const [selectedShowLink, setSelectedShowLink] = useState(null);
  const [episodeSourceOpen, setEpisodeSourceOpen] = useState(false);
  const [episodeSourceData, setEpisodeSourceData] = useState(null);
  const [episodeSourceQuery, setEpisodeSourceQuery] = useState("");
  const [selectedEpisodeSource, setSelectedEpisodeSource] = useState(null);
  const [sourceUnavailableReview, setSourceUnavailableReview] = useState([]);
  const [sourceUnavailableOpen, setSourceUnavailableOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, m, d] = await Promise.all([
        axios.get(`${apiBase}/mega-tv-sync/status`, { headers: authHeaders() }),
        axios.get(`${apiBase}/mega-tv-sync/mappings`, { headers: authHeaders() }),
        axios.get(`${apiBase}/mega-tv-sync/dashboard`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setMappings(m.data?.mappings || []);
      setDashboard(d.data || null);
    } catch (e) {
      append(`> TV SYNC ERROR: ${e.response?.data?.detail || e.message}`);
    }
  }, [apiBase, authHeaders, append]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const loadUnparsed = async (openQueue = true) => {
    setUnparsedLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/mega-tv-sync/content-review`, {
        headers: authHeaders(),
      });
      setUnparsedItems(data.items || []);
      if (openQueue) setUnparsedOpen(true);
      append(`> TV CONTENT REVIEW — ${data.file_count || 0} FILE(S) ACROSS ${data.mapping_count || 0} SHOW(S)`);
    } catch (e) {
      append(`> TV UNPARSED REVIEW ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUnparsedLoading(false);
    }
  };

  const copyPath = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      append(`> COPIED MEGA PATH — ${path}`);
    } catch {
      append(`> COPY FAILED — ${path}`);
    }
  };

  const loadMetadataHealth = async (openPanel = true) => {
    setMetadataBusy(true);
    try {
      const { data } = await axios.get(`${apiBase}/mega-tv-sync/metadata-health`, {
        headers: authHeaders(),
      });
      setMetadataHealth(data);
      if (openPanel) setMetadataOpen(true);
      append(`> TMDB METADATA HEALTH — ${data.totals?.needs_attention || 0} TAPE(S) NEED ATTENTION`);
    } catch (e) {
      append(`> TMDB METADATA HEALTH ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const appendUnresolvedMetadataItems = (items = []) => {
    items.forEach((item) => {
      const local = item.season_number != null && item.episode_number != null
        ? `S${String(item.season_number).padStart(2, '0')}E${String(item.episode_number).padStart(2, '0')}`
        : 'S??E??';
      const lookup = item.lookup_season != null && item.lookup_episode != null
        ? ` → TMDB S${String(item.lookup_season).padStart(2, '0')}E${String(item.lookup_episode).padStart(2, '0')}`
        : '';
      const show = item.show_title ? `${item.show_title} — ` : '';
      const title = item.title ? ` — ${item.title}` : '';
      append(`>   UNRESOLVED: ${show}${local}${lookup}${title} · REASON ${item.reason || 'unknown'}`);
    });
  };

  const retryMetadata = async (mappingId = null) => {
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/metadata-retry`,
        {},
        {
          params: mappingId ? { mapping_id: mappingId } : {},
          headers: authHeaders(),
        }
      );
      append(`> TMDB METADATA RETRY — ATTEMPTED ${data.attempted || 0}, UPDATED ${data.updated || 0}, UNRESOLVED ${data.unresolved || 0}, SKIPPED ${data.skipped || 0}`);
      appendUnresolvedMetadataItems(data.unresolved_items || []);
      await loadMetadataHealth(false);
      await load();
    } catch (e) {
      append(`> TMDB METADATA RETRY ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const auditMetadataAvailability = async (mappingId = null) => {
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/metadata-availability-audit`,
        {},
        { params: mappingId ? { mapping_id: mappingId } : {}, headers: authHeaders() }
      );
      append(`> TMDB AVAILABILITY AUDIT — ATTEMPTED ${data.attempted || 0}, RESOLVED ${data.resolved || 0}, SOURCE-UNAVAILABLE ${data.source_unavailable || 0}, UNRESOLVED ${data.unresolved || 0}`);
      appendUnresolvedMetadataItems(data.unresolved_items || []);
      const reviewable = (data.unresolved_items || []).filter((item) => item.reason === "tmdb_episode_not_found");
      setSourceUnavailableReview(reviewable);
      setSourceUnavailableOpen(reviewable.length > 0);
      if (reviewable.length) {
        append(`> SOURCE-UNAVAILABLE REVIEW — ${reviewable.length} EXACT TMDB EPISODE LOOKUP(S) CAN BE REVIEWED`);
      }
      await loadMetadataHealth(false);
      await load();
    } catch (e) {
      append(`> TMDB AVAILABILITY AUDIT ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };


  const markSourceUnavailable = async (item) => {
    if (!item?.video_id || !item?.mapping_id) return;
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/mappings/${item.mapping_id}/metadata-source-unavailable-exception`,
        { video_id: item.video_id },
        { headers: authHeaders() }
      );
      const local = data.season_number != null && data.episode_number != null
        ? `S${String(data.season_number).padStart(2, '0')}E${String(data.episode_number).padStart(2, '0')}`
        : 'S??E??';
      append(`> SOURCE UNAVAILABLE SAVED — ${data.show_title || ''} ${local} — ${data.title || ''}; LOCAL TITLES/ORDER UNCHANGED`);
      setSourceUnavailableReview((rows) => rows.filter((row) => row.video_id !== item.video_id));
      await loadMetadataHealth(false);
      await load();
    } catch (e) {
      append(`> SOURCE UNAVAILABLE ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };


  const repairRelinkedTapes = async (mappingId) => {
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/mappings/${mappingId}/relink-repair`,
        {},
        { headers: authHeaders() }
      );
      append(`> RELINK REPAIR — ATTEMPTED ${data.attempted || 0}, UPDATED ${data.updated || 0}, UNRESOLVED ${data.unresolved || 0}, SKIPPED ${data.skipped || 0}, REMAINING ${data.remaining_mismatch || 0}`);
      await loadMetadataHealth(false);
      await load();
    } catch (e) {
      append(`> RELINK REPAIR ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const searchShowLink = async (mappingId, customQuery = null) => {
    setMetadataBusy(true);
    try {
      const params = {};
      if (customQuery && customQuery.trim()) params.query = customQuery.trim();
      const { data } = await axios.get(
        `${apiBase}/mega-tv-sync/mappings/${mappingId}/show-link-candidates`,
        { params, headers: authHeaders() }
      );
      setShowLinkData(data);
      setShowLinkQuery(data.query || "");
      setSelectedShowLink(null);
      setShowLinkOpen(true);
      append(`> TMDB SHOW LINK REVIEW — ${data.candidates?.length || 0} TV CANDIDATE(S) FOR ${data.local_show_title}`);
    } catch (e) {
      append(`> TMDB SHOW LINK ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  useEffect(() => {
    if (!showLinkRequest?.mapping_id) return;
    setOpen(true);
    searchShowLink(showLinkRequest.mapping_id, showLinkRequest.query || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLinkRequest?.token]);

  useEffect(() => {
    if (!showLinkOpen || !showLinkData) return;
    const frame = window.requestAnimationFrame(() => {
      const review = document.querySelector('[data-testid="tmdb-show-link-review"]');
      review?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showLinkOpen, showLinkData]);

  const saveShowLink = async () => {
    if (!showLinkData || !selectedShowLink) return;
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/mappings/${showLinkData.mapping_id}/show-link`,
        { tmdb_id: selectedShowLink.tmdb_id },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> TMDB SHOW LINK SAVED — ${showLinkData.local_show_title} → ${data.tmdb_show_title} (${data.tmdb_id})`);
      const repairedMappingId = showLinkData.mapping_id;
      const repairedShowTitle = showLinkData.local_show_title;
      setShowLinkOpen(false);
      setShowLinkData(null);
      setSelectedShowLink(null);

      append(`> MEDIA REVIEW — RECONCILING ${repairedShowTitle} AFTER TMDB LINK CHANGE...`);
      const reconcile = await axios.post(
        `${apiBase}/media-ingest/review/tv-mapping/${encodeURIComponent(repairedMappingId)}/reconcile`,
        {},
        { headers: authHeaders() }
      );
      append(`> MEDIA REVIEW — ${reconcile.data?.metadata?.matched ? "TMDB REPAIR COMPLETE" : "TMDB REPAIR RECHECKED"}: ${repairedShowTitle}`);
      await loadMetadataHealth(false);
      await load();
      onShowLinkResolved?.({
        mapping_id: repairedMappingId,
        reconcile: reconcile.data,
        review: reconcile.data?.review || null,
      });
    } catch (e) {
      append(`> TMDB SHOW LINK SAVE ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const searchEpisodeSource = async (mappingId, customQuery = null) => {
    setMetadataBusy(true);
    try {
      const params = {};
      if (customQuery && customQuery.trim()) params.query = customQuery.trim();
      const { data } = await axios.get(
        `${apiBase}/mega-tv-sync/mappings/${mappingId}/episode-source-candidates`,
        { params, headers: authHeaders() }
      );
      setEpisodeSourceData(data);
      setEpisodeSourceQuery(data.query || "");
      setSelectedEpisodeSource(null);
      setEpisodeSourceOpen(true);
      append(`> TMDB EPISODE SOURCE REVIEW — ${data.candidates?.length || 0} TV CANDIDATE(S) FOR ${data.local_show_title}`);
    } catch (e) {
      append(`> TMDB EPISODE SOURCE ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const saveEpisodeSource = async (clear = false) => {
    if (!episodeSourceData || (!clear && !selectedEpisodeSource)) return;
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/mappings/${episodeSourceData.mapping_id}/episode-source`,
        { tmdb_id: clear ? null : selectedEpisodeSource.tmdb_id },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      if (clear) {
        append(`> TMDB EPISODE SOURCE CLEARED — ${episodeSourceData.local_show_title} WILL USE ITS PRIMARY SHOW LINK`);
      } else {
        append(`> TMDB EPISODE SOURCE SAVED — ${episodeSourceData.local_show_title} EPISODES → ${data.tmdb_show_title} (${data.tmdb_id}); LOCAL SHOW IDENTITY UNCHANGED`);
      }
      setEpisodeSourceOpen(false);
      setEpisodeSourceData(null);
      setSelectedEpisodeSource(null);
      await loadMetadataHealth(false);
      await load();
    } catch (e) {
      append(`> TMDB EPISODE SOURCE SAVE ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const previewNumbering = async (mappingId) => {
    setMetadataBusy(true);
    try {
      const { data } = await axios.get(
        `${apiBase}/mega-tv-sync/mappings/${mappingId}/numbering-preview`,
        { headers: authHeaders() }
      );
      setNumberingPreview(data);
      setNumberingOpen(true);
      append(`> NUMBERING PREVIEW — ${data.proposed_rules?.length || 0} RULE(S), ${data.confirmed_rule_count || 0} TITLE-CONFIRMED, ${data.inferred_rule_count || 0} OFFSET-INFERRED, ${data.matched_count || 0} TITLE MATCH(ES), ${data.unmatched_count || 0} UNMATCHED`);
    } catch (e) {
      append(`> NUMBERING PREVIEW ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const applyNumberingRules = async () => {
    if (!numberingPreview) return;
    setMetadataBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-tv-sync/mappings/${numberingPreview.mapping_id}/numbering-apply`,
        { rules: numberingPreview.proposed_rules || [] },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> NUMBERING MAP SAVED — ${(data.rules || []).length} LOOKUP RULE(S); LOCAL TITLES/ORDER UNCHANGED`);
      setNumberingPreview((prev) => prev ? { ...prev, current_rules: data.rules || [] } : prev);
      await retryMetadata(numberingPreview.mapping_id);
    } catch (e) {
      append(`> NUMBERING MAP ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMetadataBusy(false);
    }
  };

  const health = dashboard?.health === "healthy" ? "ALL SYSTEMS OK"
    : dashboard?.health === "attention" ? "ATTENTION"
    : dashboard?.health === "review" ? "REVIEW NEEDED"
    : dashboard?.health === "warming_up" ? "INITIALISING" : "IDLE";

  const healthDate = (value) => (
    value ? new Date(value).toLocaleString() : "NEVER"
  );

  const filteredHealthRows = (dashboard?.show_health || []).filter((row) => {
    if (healthFilter === "ALL") return true;
    if (healthFilter === "HEALTHY") return row.state === "healthy";
    if (healthFilter === "REVIEW") return row.state === "review";
    if (healthFilter === "ERROR") return row.state === "error";
    if (healthFilter === "WAITING") return row.state === "waiting";
    return true;
  });

  return (
    <div className="border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2" data-testid="mega-tv-sync-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] tracking-widest" data-testid="mega-tv-sync-heading">
          TV — STATUS / REVIEW / REPAIR
        </div>
        {open && <span className="text-[9px] tracking-widest">AUTOMATIC INGEST — {health}</span>}
      </div>

      {open && (
        <div className="mt-2 space-y-3">
          <div className="text-[9px] opacity-70 leading-relaxed">
            AUTOMATIC S4 TV INGEST: SHOW FOLDER → CHANNEL → TMDB → EPISODES → DURATION → GUIDE.
            <br />
            SHOW FOLDERS ARE CHECKED ABOUT EVERY 5 MINUTES. USE THE TOOLS BELOW ONLY FOR STATUS, REVIEW OR EXCEPTIONAL REPAIR.
          </div>

          {dashboard && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_42%,transparent)] p-2" data-testid="tv-sync-health-centre">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TV INGEST HEALTH</div>
                  <div className="text-[8px] opacity-55 mt-1">
                    SYSTEM-WIDE STATUS FOR AUTOMATICALLY MANAGED TV SHOWS.
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => loadMetadataHealth(true)}
                    disabled={metadataBusy}
                    className={btn}
                  >
                    TMDB METADATA HEALTH
                  </button>
                  <button
                    type="button"
                    onClick={() => setHealthOpen((v) => !v)}
                    className={btn}
                  >
                    {healthOpen ? "HIDE SHOW HEALTH" : "SHOW HEALTH DETAILS"}
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                {[
                  ["STATUS", health],
                  ["SHOW MAPS", `${dashboard.enabled_mapping_count || 0}/${dashboard.mapping_count || 0}`],
                  ["FILES WATCHED", dashboard.files_watched || 0],
                  ["SYNCED TAPES", dashboard.synced_tapes || 0],
                  ["EPISODES", dashboard.episodes || 0],
                  ["SPECIALS", dashboard.specials || 0],
                  ["EXTRAS", dashboard.extras || 0],
                  ["MOVIES", dashboard.movies || 0],
                ].map(([k, v]) => (
                  <div key={k} className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                    <div className="text-[8px] opacity-55">{k}</div>
                    <div className="text-[12px]">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                <button
                  type="button"
                  onClick={() => loadUnparsed(true)}
                  className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2 text-left hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)]"
                >
                  <div className="text-[8px] opacity-55">UNKNOWN</div>
                  <div className="text-[12px]">{dashboard.unknown || 0}</div>
                  <div className="text-[7px] opacity-45">OPEN REVIEW</div>
                </button>

                {[
                  ["FAILURES", dashboard.failed || 0],
                  ["PROBLEM SHOWS", dashboard.problem_mappings || 0],
                  ["TOTAL SCANS", dashboard.lifetime_scans || 0],
                  ["LIFETIME ADDED", dashboard.lifetime_added || 0],
                  ["ADOPTED", dashboard.lifetime_adopted || 0],
                  ["TMDB EP", dashboard.lifetime_tmdb_episodes || 0],
                  ["WAITING", dashboard.never_scanned || 0],
                ].map(([k, v]) => (
                  <div key={k} className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                    <div className="text-[8px] opacity-55">{k}</div>
                    <div className="text-[12px]">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-2 flex gap-x-5 gap-y-1 flex-wrap text-[8px] opacity-60">
                <span>LAST SCAN: {healthDate(dashboard.last_scan_at)}</span>
                <span>LAST SUCCESS: {healthDate(dashboard.last_success_at)}</span>
              </div>

              {healthOpen && (
                <div className="mt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] pt-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[9px] tracking-widest">SHOW HEALTH</div>
                    <div className="flex gap-1 flex-wrap">
                      {["ALL", "ERROR", "REVIEW", "WAITING", "HEALTHY"].map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setHealthFilter(name)}
                          className={`${btn} ${healthFilter === name ? "bg-[var(--phosphor)] text-black" : ""}`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2 space-y-1 max-h-[420px] overflow-auto pr-1">
                    {filteredHealthRows.map((row) => (
                      <div
                        key={row.mapping_id}
                        className="grid grid-cols-[100px_minmax(180px,1fr)_repeat(6,70px)] gap-2 items-center border-t border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] py-1"
                      >
                        <div className="text-[8px] tracking-widest">{String(row.state || "").toUpperCase()}</div>
                        <div className="min-w-0">
                          <div className="text-[9px] truncate">{row.show_title}</div>
                          <div className="text-[7px] opacity-45 truncate">
                            {row.channel ? `CH ${row.channel.number} — ${row.channel.name}` : "CHANNEL ?"}
                          </div>
                        </div>
                        <div className="text-[8px] text-right">FILES {row.found || 0}</div>
                        <div className="text-[8px] text-right">EP {row.episodes || 0}</div>
                        <div className="text-[8px] text-right">SP {row.specials || 0}</div>
                        <div className="text-[8px] text-right">EX {row.extras || 0}</div>
                        <div className="text-[8px] text-right">MOV {row.movies || 0}</div>
                        <div className="text-[8px] text-right">UNK {row.unknown || 0}</div>
                      </div>
                    ))}
                    {!filteredHealthRows.length && (
                      <div className="text-[8px] opacity-50 py-2">NO SHOWS IN THIS FILTER.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {metadataOpen && metadataHealth && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="tmdb-metadata-health">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TMDB METADATA HEALTH</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    TARGETED METADATA CHECK — NO MEGA RESCAN OR RE-EXPORT REQUIRED.
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => auditMetadataAvailability(null)} disabled={metadataBusy} className={btn}>
                    AUDIT AVAILABILITY
                  </button>
                  <button type="button" onClick={() => retryMetadata(null)} disabled={metadataBusy} className={btn}>
                    RETRY ALL GAPS
                  </button>
                  <button type="button" onClick={() => loadMetadataHealth(false)} disabled={metadataBusy} className={btn}>
                    REFRESH
                  </button>
                  <button type="button" onClick={() => setMetadataOpen(false)} className={btn}>
                    CLOSE
                  </button>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                {[
                  ["MANAGED", metadataHealth.totals?.managed_tapes || 0],
                  ["HEALTHY", metadataHealth.totals?.healthy || 0],
                  ["NEEDS ATTENTION", metadataHealth.totals?.needs_attention || 0],
                  ["SOURCE UNAVAILABLE", metadataHealth.totals?.source_unavailable || 0],
                  ["EXPECTED LIGHT", metadataHealth.totals?.expected_metadata_light || 0],
                  ["NO TMDB LINK", metadataHealth.totals?.no_tmdb_link || 0],
                  ["NO STILL", metadataHealth.totals?.no_still || 0],
                  ["NO POSTER", metadataHealth.totals?.no_poster || 0],
                  ["NO DESCRIPTION", metadataHealth.totals?.no_description || 0],
                  ["RELINK MISMATCH", metadataHealth.totals?.relink_mismatch || 0],
                ].map(([k, v]) => (
                  <div key={k} className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                    <div className="text-[8px] opacity-55">{k}</div>
                    <div className="text-[12px]">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 text-[9px] tracking-widest">SHOWS NEEDING METADATA ATTENTION</div>
              <div className="mt-1 space-y-1 max-h-[420px] overflow-auto pr-1">
                {(metadataHealth.shows || []).map((row) => (
                  <div
                    key={row.mapping_id}
                    className="grid grid-cols-[minmax(180px,1fr)_repeat(7,78px)_360px] gap-2 items-center border-t border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] py-1"
                  >
                    <div className="min-w-0">
                      <div className="text-[9px] truncate">{row.show_title}</div>
                      <div className="text-[7px] opacity-45 truncate">
                        {row.channel ? `CH ${row.channel.number} — ${row.channel.name}` : "CHANNEL ?"}
                      </div>
                    </div>
                    <div className="text-[8px] text-right">GAPS {row.count || 0}</div>
                    <div className="text-[8px] text-right">LINK {row.no_tmdb_link || 0}</div>
                    <div className="text-[8px] text-right">STILL {row.no_still || 0}</div>
                    <div className="text-[8px] text-right">POSTER {row.no_poster || 0}</div>
                    <div className="text-[8px] text-right">DESC {row.no_description || 0}</div>
                    <div className="text-[8px] text-right">UNAV {row.source_unavailable || 0}</div>
                    <div className="text-[8px] text-right">MISMATCH {row.relink_mismatch || 0}</div>
                    <div className="flex gap-1">
                      {!row.tmdb_id ? (
                        <button
                          type="button"
                          onClick={() => searchShowLink(row.mapping_id)}
                          disabled={metadataBusy}
                          className={btn}
                        >
                          LINK SHOW
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => previewNumbering(row.mapping_id)}
                            disabled={metadataBusy}
                            className={btn}
                          >
                            NUMBERING
                          </button>
                          <button
                            type="button"
                            onClick={() => searchShowLink(row.mapping_id)}
                            disabled={metadataBusy}
                            className={btn}
                          >
                            CHANGE LINK
                          </button>
                          <button
                            type="button"
                            onClick={() => searchEpisodeSource(row.mapping_id)}
                            disabled={metadataBusy}
                            className={btn}
                            title="Use a different TMDb TV series only as the episode metadata source"
                          >
                            EPISODE SOURCE
                          </button>
                        </>
                      )}
                      {(row.relink_mismatch || 0) > 0 && (
                        <button
                          type="button"
                          onClick={() => repairRelinkedTapes(row.mapping_id)}
                          disabled={metadataBusy || !row.metadata_numbering_trusted}
                          className={btn}
                        >
                          REPAIR RELINKED
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => auditMetadataAvailability(row.mapping_id)}
                        disabled={metadataBusy}
                        className={btn}
                      >
                        AUDIT
                      </button>
                      <button
                        type="button"
                        onClick={() => retryMetadata(row.mapping_id)}
                        disabled={metadataBusy || !row.metadata_numbering_trusted}
                        className={btn}
                        title={
                          row.metadata_numbering_trusted
                            ? "Retry TMDb metadata for this show"
                            : "Run NUMBERING preview and APPLY MAP first"
                        }
                      >
                        RETRY SHOW
                      </button>
                    </div>
                  </div>
                ))}
                {!(metadataHealth.shows || []).length && (
                  <div className="text-[8px] opacity-50 py-2">ALL MANAGED TV CONTENT HAS EXPECTED METADATA.</div>
                )}
              </div>
            </div>
          )}

          {sourceUnavailableOpen && sourceUnavailableReview.length > 0 && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="tmdb-source-unavailable-review">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TMDB SOURCE-UNAVAILABLE REVIEW</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    Exact tape exceptions only. Saving one does not change local titles, order or S/E identity.
                  </div>
                </div>
                <button type="button" onClick={() => setSourceUnavailableOpen(false)} className={btn}>CLOSE</button>
              </div>
              <div className="mt-2 space-y-2">
                {sourceUnavailableReview.map((item) => {
                  const local = item.season_number != null && item.episode_number != null
                    ? `S${String(item.season_number).padStart(2, '0')}E${String(item.episode_number).padStart(2, '0')}`
                    : 'S??E??';
                  const lookup = item.lookup_season != null && item.lookup_episode != null
                    ? `TMDB S${String(item.lookup_season).padStart(2, '0')}E${String(item.lookup_episode).padStart(2, '0')}`
                    : 'TMDB S??E??';
                  return (
                    <div key={item.video_id} className="border-t border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] pt-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[9px]">{item.show_title} — {local} → {lookup}</div>
                        <div className="text-[8px] opacity-60 truncate">{item.title}</div>
                        <div className="text-[8px] opacity-50">REASON {item.reason}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => markSourceUnavailable(item)}
                        disabled={metadataBusy}
                        className={btn}
                      >
                        MARK SOURCE UNAVAILABLE
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showLinkOpen && showLinkData && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="tmdb-show-link-review">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TMDB SHOW LINK REVIEW</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    LOCAL SHOW: {showLinkData.local_show_title}
                  </div>
                  {showLinkData.current_tmdb_id && (
                    <div className="mt-1 text-[8px] opacity-60">
                      CURRENT LINK: {showLinkData.current_tmdb_show_title || "TMDB TV"} ({showLinkData.current_tmdb_id})
                    </div>
                  )}
                  <div className="mt-1 text-[8px] opacity-55">
                    TV RESULTS ONLY. {showLinkData.current_tmdb_id
                      ? "THIS WILL REPLACE THE CURRENT SHOW LINK ONLY AFTER YOU CONFIRM."
                      : "NOTHING IS SAVED UNTIL YOU SELECT A CANDIDATE AND PRESS SAVE SHOW LINK."}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={saveShowLink} disabled={metadataBusy || !selectedShowLink} className={btn}>
                    {showLinkData.current_tmdb_id ? "CHANGE SHOW LINK" : "SAVE SHOW LINK"}
                  </button>
                  <button type="button" onClick={() => setShowLinkOpen(false)} className={btn}>
                    CLOSE
                  </button>
                </div>
              </div>

              <div className="mt-2 flex gap-2">
                <input
                  value={showLinkQuery}
                  onChange={(e) => setShowLinkQuery(e.target.value)}
                  className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[10px]"
                  placeholder="Search TMDb TV shows"
                />
                <button
                  type="button"
                  onClick={() => searchShowLink(showLinkData.mapping_id, showLinkQuery)}
                  disabled={metadataBusy}
                  className={btn}
                >
                  SEARCH TV
                </button>
              </div>

              <div className="mt-3 space-y-2 max-h-[520px] overflow-auto pr-1">
                {(showLinkData.candidates || []).map((candidate) => {
                  const selected = selectedShowLink?.tmdb_id === candidate.tmdb_id;
                  return (
                    <button
                      key={candidate.tmdb_id}
                      type="button"
                      onClick={() => setSelectedShowLink(candidate)}
                      className={`w-full text-left border p-2 ${
                        selected
                          ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                      }`}
                    >
                      <div className="flex gap-3 items-start">
                        {candidate.poster_url ? (
                          <img
                            src={candidate.poster_url}
                            alt=""
                            className="w-[54px] h-[78px] object-cover border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                          />
                        ) : (
                          <div className="w-[54px] h-[78px] border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] flex items-center justify-center text-[7px] opacity-50">
                            NO POSTER
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px]">
                            {selected ? "● " : "○ "}
                            {candidate.title}
                            {candidate.year ? ` (${candidate.year})` : ""}
                          </div>
                          <div className="mt-1 text-[8px] opacity-55">TMDB TV ID {candidate.tmdb_id}</div>
                          <div className="mt-2 text-[8px] opacity-70 leading-relaxed">
                            {candidate.overview || "NO DESCRIPTION AVAILABLE"}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {!(showLinkData.candidates || []).length && (
                  <div className="text-[8px] opacity-50 py-2">
                    NO TV CANDIDATES FOUND. EDIT THE SEARCH TERM AND TRY AGAIN.
                  </div>
                )}
              </div>
            </div>
          )}

          {episodeSourceOpen && episodeSourceData && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="tmdb-episode-source-review">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TMDB EPISODE SOURCE REVIEW</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    LOCAL SHOW: {episodeSourceData.local_show_title}
                  </div>
                  <div className="mt-1 text-[8px] opacity-60">
                    PRIMARY SHOW LINK: {episodeSourceData.primary_tmdb_show_title || "TMDB TV"} ({episodeSourceData.primary_tmdb_id})
                  </div>
                  {episodeSourceData.current_episode_source_tmdb_id && (
                    <div className="mt-1 text-[8px] opacity-60">
                      CURRENT EPISODE SOURCE: {episodeSourceData.current_episode_source_tmdb_show_title || "TMDB TV"} ({episodeSourceData.current_episode_source_tmdb_id})
                    </div>
                  )}
                  <div className="mt-1 text-[8px] opacity-55">
                    EPISODE LOOKUP ONLY. THE LOCAL SHOW LINK, CHANNEL, TITLES, LOCAL S/E NUMBERS AND ORDER WILL NOT CHANGE.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => saveEpisodeSource(false)} disabled={metadataBusy || !selectedEpisodeSource} className={btn}>
                    SAVE EPISODE SOURCE
                  </button>
                  {episodeSourceData.current_episode_source_tmdb_id && (
                    <button type="button" onClick={() => saveEpisodeSource(true)} disabled={metadataBusy} className={btn}>
                      USE PRIMARY SHOW
                    </button>
                  )}
                  <button type="button" onClick={() => setEpisodeSourceOpen(false)} className={btn}>
                    CLOSE
                  </button>
                </div>
              </div>

              <div className="mt-2 flex gap-2">
                <input
                  value={episodeSourceQuery}
                  onChange={(e) => setEpisodeSourceQuery(e.target.value)}
                  className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] px-2 py-1 text-[10px]"
                  placeholder="Search parent / episode-source TMDb TV show"
                />
                <button
                  type="button"
                  onClick={() => searchEpisodeSource(episodeSourceData.mapping_id, episodeSourceQuery)}
                  disabled={metadataBusy}
                  className={btn}
                >
                  SEARCH TV
                </button>
              </div>

              <div className="mt-3 space-y-2 max-h-[520px] overflow-auto pr-1">
                {(episodeSourceData.candidates || []).map((candidate) => {
                  const selected = selectedEpisodeSource?.tmdb_id === candidate.tmdb_id;
                  return (
                    <button
                      key={candidate.tmdb_id}
                      type="button"
                      onClick={() => setSelectedEpisodeSource(candidate)}
                      className={`w-full text-left border p-2 ${
                        selected
                          ? "border-[var(--phosphor)] bg-[color-mix(in_srgb,var(--phosphor)_10%,transparent)]"
                          : "border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                      }`}
                    >
                      <div className="flex gap-3 items-start">
                        {candidate.poster_url ? (
                          <img
                            src={candidate.poster_url}
                            alt=""
                            className="w-[54px] h-[78px] object-cover border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)]"
                          />
                        ) : (
                          <div className="w-[54px] h-[78px] border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] flex items-center justify-center text-[7px] opacity-50">
                            NO POSTER
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px]">
                            {selected ? "● " : "○ "}
                            {candidate.title}
                            {candidate.year ? ` (${candidate.year})` : ""}
                          </div>
                          <div className="mt-1 text-[8px] opacity-55">TMDB TV ID {candidate.tmdb_id}</div>
                          <div className="mt-2 text-[8px] opacity-70 leading-relaxed">
                            {candidate.overview || "NO DESCRIPTION AVAILABLE"}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
                {!(episodeSourceData.candidates || []).length && (
                  <div className="text-[8px] opacity-50 py-2">
                    NO TV CANDIDATES FOUND. EDIT THE SEARCH TERM AND TRY AGAIN.
                  </div>
                )}
              </div>
            </div>
          )}

          {numberingOpen && numberingPreview && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="numbering-reconciliation">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">EPISODE NUMBERING RECONCILIATION</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    {numberingPreview.show_title} → EPISODE SOURCE TMDB {numberingPreview.episode_source_tmdb_show_title || numberingPreview.episode_source_tmdb_id}
                    {numberingPreview.episode_source_is_parent ? ` (${numberingPreview.episode_source_tmdb_id}; PRIMARY SHOW LINK UNCHANGED)` : ""}
                  </div>
                  <div className="mt-1 text-[8px] opacity-55">
                    LOOKUP-ONLY MAPPING. LOCAL S/E NUMBERS, TITLES AND CHANNEL ORDER WILL NOT CHANGE.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={applyNumberingRules} disabled={metadataBusy || !(numberingPreview.proposed_rules || []).length} className={btn}>
                    APPLY MAP + RETRY
                  </button>
                  <button type="button" onClick={() => setNumberingOpen(false)} className={btn}>
                    CLOSE
                  </button>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">TITLE MATCHES</div>
                  <div className="text-[12px]">{numberingPreview.matched_count || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">UNMATCHED</div>
                  <div className="text-[12px]">{numberingPreview.unmatched_count || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">TITLE-CONFIRMED</div>
                  <div className="text-[12px]">{numberingPreview.confirmed_rule_count || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">OFFSET-INFERRED</div>
                  <div className="text-[12px]">{numberingPreview.inferred_rule_count || 0}</div>
                </div>
              </div>

              <div className="mt-3 text-[9px] tracking-widest">PROPOSED LOOKUP RULES</div>
              <div className="mt-1 space-y-1">
                {(numberingPreview.proposed_rules || []).map((rule, idx) => (
                  <div key={`${rule.local_season}-${rule.local_episode_start}-${idx}`} className="border-t border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)] py-1 text-[8px]">
                    LOCAL S{String(rule.local_season).padStart(2, "0")}E{String(rule.local_episode_start).padStart(2, "0")}
                    {rule.local_episode_end !== rule.local_episode_start ? `–E${String(rule.local_episode_end).padStart(2, "0")}` : ""}
                    {" → "}
                    TMDB S{String(rule.tmdb_season).padStart(2, "0")}
                    {rule.rule_type === "stride" ? (
                      <>
                        {" with episode stride ×"}{rule.episode_multiplier}
                        {rule.episode_offset ? ` ${rule.episode_offset >= 0 ? "+" : ""}${rule.episode_offset}` : ""}
                      </>
                    ) : (
                      <>
                        {" with episode offset "}
                        {rule.episode_offset >= 0 ? `+${rule.episode_offset}` : rule.episode_offset}
                      </>
                    )}
                    {" · "}
                    {rule.evidence_type === "offset_inferred" ? "OFFSET-INFERRED" : rule.rule_type === "stride" ? "TITLE-CONFIRMED STRIDE" : "TITLE-CONFIRMED"}
                    {" · evidence "}
                    {rule.evidence_count}
                    {" · confidence "}
                    {Math.round((rule.average_confidence || 0) * 100)}%
                  </div>
                ))}
                {!(numberingPreview.proposed_rules || []).length && (
                  <div className="text-[8px] opacity-50 py-2">
                    NO SAFE CONSISTENT NUMBERING RULES COULD BE DERIVED.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 2.3.65.1-r1.1
              Discover/map, enable/disable, per-map scan and remove controls are
              intentionally hidden from normal operation. Automatic S4 TV ingest
              owns channel/mapping reconciliation. Existing mappings remain intact. */}
        </div>
      )}
    </div>
  );
}
